import React, { useEffect, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { stripeReconcileAPI, customersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { RefreshCw, CreditCard, CheckCircle2, X, Search, Loader2, Mail, Phone, MapPin, User } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const MATCH_BADGE = {
  confident: { label: 'Confident match', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  multiple_invoices: { label: 'Pick invoice', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  multiple_customers: { label: 'Multiple customers', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  identity_only: { label: 'No amount match', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  no_match: { label: 'No match', cls: 'bg-red-100 text-red-600 border-red-200' },
};

const METHODS = [
  { value: 'card', label: 'Card (Stripe)' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const PendingRow = ({ pay, onDone }) => {
  const [customerId, setCustomerId] = useState(pay.suggested_customer_id || '');
  const [customerName, setCustomerName] = useState(pay.suggested_customer_name || '');
  const [invoiceId, setInvoiceId] = useState(pay.suggested_invoice_id || '');
  const [method, setMethod] = useState('card');
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadInvoices = useCallback(async (cid) => {
    if (!cid) { setInvoices([]); return; }
    try {
      const res = await stripeReconcileAPI.customerOpenInvoices(cid);
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setInvoices([]); }
  }, []);

  useEffect(() => { if (customerId) loadInvoices(customerId); }, [customerId, loadInvoices]);

  const doSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const res = await customersAPI.getAll({ search: searchTerm.trim(), limit: 10 });
      const list = Array.isArray(res.data) ? res.data : (res.data?.customers || res.data?.items || []);
      setSearchResults(list.slice(0, 10));
    } catch (e) {
      toast.error('Customer search failed');
    } finally { setSearching(false); }
  };

  const pickCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerName(`${c.first_name || ''} ${c.last_name || ''}`.trim());
    setInvoiceId('');
    setSearchResults([]);
    setSearchTerm('');
  };

  const confirm = async () => {
    if (!customerId || !invoiceId) { toast.error('Select a customer and an invoice'); return; }
    setBusy(true);
    try {
      await stripeReconcileAPI.confirm(pay.id, { customer_id: customerId, invoice_id: invoiceId, payment_method: method });
      toast.success('Payment reconciled — invoice marked paid on CRM, portal & Xero');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not confirm payment');
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await stripeReconcileAPI.dismiss(pay.id);
      toast.success('Payment dismissed');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not dismiss');
    } finally { setBusy(false); }
  };

  const badge = MATCH_BADGE[pay.match_type] || MATCH_BADGE.no_match;

  return (
    <Card className="border-slate-200" data-testid={`pending-payment-${pay.id}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-slate-900 font-['Manrope']" data-testid={`pending-amount-${pay.id}`}>
                {formatCurrency(pay.amount)}
              </span>
              <Badge variant="outline" className={badge.cls} data-testid={`pending-matchtype-${pay.id}`}>{badge.label}</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1">Paid {formatDate(pay.created)} via Stripe Payment Link</p>
          </div>
          <div className="text-sm text-slate-600 space-y-0.5">
            {pay.payer_name && <p className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-slate-400" />{pay.payer_name}</p>}
            {pay.payer_email && <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" />{pay.payer_email}</p>}
            {pay.payer_phone && <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" />{pay.payer_phone}</p>}
            {pay.payer_postcode && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{pay.payer_postcode}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
          {/* Customer */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500">Customer</label>
            {customerId ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm font-medium text-slate-800 truncate" data-testid={`pending-customer-${pay.id}`}>{customerName || customerId}</span>
                <button className="text-slate-400 hover:text-red-500" onClick={() => { setCustomerId(''); setCustomerName(''); setInvoiceId(''); }} data-testid={`pending-customer-clear-${pay.id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex gap-1.5">
                  <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()} placeholder="Search name / email" className="h-9" data-testid={`pending-search-${pay.id}`} />
                  <Button size="icon" variant="outline" className="h-9 w-9 flex-shrink-0" onClick={doSearch} disabled={searching} data-testid={`pending-search-btn-${pay.id}`}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {searchResults.length > 0 && (
                  <div className="rounded-md border border-slate-200 bg-white max-h-40 overflow-auto">
                    {searchResults.map((c) => (
                      <button key={c.id} onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0" data-testid={`pending-result-${c.id}`}>
                        {c.first_name} {c.last_name}
                        <span className="text-xs text-slate-400 ml-2">{c.postcode || c.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Invoice */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500">Invoice to settle</label>
            <Select value={invoiceId} onValueChange={setInvoiceId} disabled={!customerId}>
              <SelectTrigger className="h-9" data-testid={`pending-invoice-${pay.id}`}>
                <SelectValue placeholder={customerId ? 'Select invoice' : 'Pick customer first'} />
              </SelectTrigger>
              <SelectContent>
                {invoices.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No open invoices</div>}
                {invoices.map((inv) => (
                  <SelectItem key={inv.id} value={inv.id}>
                    {(inv.xero_invoice_number || 'Invoice')} — {formatCurrency(inv.amount)}
                    {inv.invoice_type ? ` (${inv.invoice_type.replace('_', ' ')})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Method */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500">Payment method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-9" data-testid={`pending-method-${pay.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={dismiss} disabled={busy} className="text-slate-500 hover:text-red-600" data-testid={`pending-dismiss-${pay.id}`}>
            <X className="w-4 h-4 mr-1" /> Dismiss
          </Button>
          <Button onClick={confirm} disabled={busy || !customerId || !invoiceId} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pending-confirm-${pay.id}`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Confirm paid
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function StripePayments() {
  const [payments, setPayments] = useState([]);
  const [history, setHistory] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [tab, setTab] = useState('pending');
  const [autoApply, setAutoApply] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await stripeReconcileAPI.pending();
      setPayments(res.data?.payments || []);
      setMeta({ last_scan_at: res.data?.last_scan_at, active_since: res.data?.active_since, configured: res.data?.configured });
      setAutoApply(!!res.data?.auto_apply);
    } catch (e) {
      toast.error('Could not load pending payments');
    } finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await stripeReconcileAPI.history();
      setHistory(res.data?.payments || []);
    } catch (e) { setHistory([]); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await stripeReconcileAPI.scan();
      if (res.data?.activated) {
        toast.success('Reconciliation activated — new Payment Link payments from now on will appear here.');
      } else {
        const n = res.data?.imported || 0;
        const auto = res.data?.auto_applied || 0;
        toast.success(`Scan complete — ${n} new payment(s)${auto ? `, ${auto} auto-applied` : ''}.`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const toggleAutoApply = async (val) => {
    setSavingToggle(true);
    setAutoApply(val);
    try {
      await stripeReconcileAPI.saveSettings({ auto_apply: val });
      toast.success(val ? 'Auto-apply on — confident single matches will settle automatically.' : 'Auto-apply off — all matches need manual confirmation.');
    } catch (e) {
      setAutoApply(!val);
      toast.error('Could not save setting');
    } finally { setSavingToggle(false); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="stripe-payments-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-emerald-600" /> Stripe Payment Link Payments
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Review and reconcile payments taken via Stripe Payment Links. Confirming marks the invoice paid on the CRM, customer portal and Xero.
            </p>
          </div>
          <Button onClick={scan} disabled={scanning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="stripe-scan-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning…' : 'Scan Stripe now'}
          </Button>
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-slate-800">Auto-apply confident matches</p>
            <p className="text-xs text-slate-500">When on, a payment that matches exactly one customer and one invoice settles automatically — no review needed.</p>
          </div>
          <Switch checked={autoApply} onCheckedChange={toggleAutoApply} disabled={savingToggle} data-testid="stripe-autoapply-toggle" />
        </div>

        {meta.last_scan_at && (
          <p className="text-xs text-slate-400" data-testid="stripe-last-scan">Last scanned {formatDate(meta.last_scan_at)}</p>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="stripe-tabs">
            <TabsTrigger value="pending" data-testid="stripe-tab-pending">Pending ({payments.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="stripe-tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : payments.length === 0 ? (
              <Card className="border-dashed border-slate-300">
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium" data-testid="stripe-empty">No payments waiting to be reconciled</p>
                  <p className="text-sm text-slate-400 mt-1">New Stripe Payment Link payments will appear here automatically.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {payments.map((p) => <PendingRow key={p.id} pay={p} onDone={load} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            {history.length === 0 ? (
              <p className="text-sm text-slate-400 italic" data-testid="stripe-history-empty">No reconciled or dismissed payments yet.</p>
            ) : (
              <div className="space-y-2" data-testid="stripe-history-list">
                {history.map((h) => (
                  <Card key={h.id} className="border-slate-200" data-testid={`history-row-${h.id}`}>
                    <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{formatCurrency(h.amount)} <span className="font-normal text-slate-500">· {h.payer_name || h.payer_email || 'Unknown payer'}</span></p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Paid {formatDate(h.created)}
                          {h.status === 'confirmed' && h.applied_payment_method ? ` · via ${h.applied_payment_method.replace('_', ' ')}` : ''}
                          {h.confirmed_by ? ` · by ${h.confirmed_by}` : ''}
                          {h.status === 'dismissed' && h.dismissed_by ? ` · by ${h.dismissed_by}` : ''}
                        </p>
                      </div>
                      <Badge variant="outline" className={h.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'} data-testid={`history-status-${h.id}`}>
                        {h.status === 'confirmed' ? 'Reconciled' : 'Dismissed'}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
